/**
 * The screen's safety net. It sits inside the shell around the routed screen, so when a screen throws while it
 * draws (or its code fails to download), the nav stays usable and only the screen's area shows "This screen hit an
 * error" with Try again, Go back and Reload. Opening another screen clears it.
 *
 * In development it shows the error and the component stack. In production it shows a short reference, which is
 * also in the report sent to /api/client-errors, so the server log line can be matched to what the person saw.
 */
import { Component, useCallback, type ErrorInfo, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Button, ErrorNote, Text } from '../ui'
import { reportError, type ReportContext } from './reportError'
import { browserRecovery, isChunkLoadError, isReloading, recoverFromStaleChunk } from './staleChunk'

export interface ScreenBoundaryProps {
  /** When this changes (the path), a shown error clears. */
  resetKey: string
  children: ReactNode
  onBack: () => void
  /** Show the error and component stack. Defaults to Vite's development flag. */
  dev?: boolean
  /** Tests pass fakes for these two. */
  report?: (error: unknown, context: ReportContext) => Promise<void> | undefined
  recoverStale?: (error: unknown) => boolean
}

interface State {
  error: Error | null
  ref: string
  /** The screen's code file is gone: a new version was deployed. */
  stale: boolean
  /** A reload to the new version is on its way. */
  reloading: boolean
  componentStack?: string
}

const clear: State = { error: null, ref: '', stale: false, reloading: false }

/** Eight characters, easy to read out: no 0/o or 1/l. */
export function newRef(): string {
  const alphabet = '23456789abcdefghjkmnpqrstuvwxyz'
  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

export class ScreenBoundary extends Component<ScreenBoundaryProps, State> {
  override state: State = clear

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const err = error instanceof Error ? error : new Error(String(error))
    const reloading = isReloading()
    return { error: err, ref: newRef(), stale: reloading || isChunkLoadError(err), reloading }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    const componentStack = info.componentStack ?? undefined
    if (this.state.reloading) return // the stale-chunk handler already reported it and is reloading
    if (this.state.stale) {
      const recover = this.props.recoverStale ?? ((e: unknown) => recoverFromStaleChunk(e, browserRecovery(reportError)))
      this.setState({ componentStack, reloading: recover(error) })
      return
    }
    const report = this.props.report ?? reportError
    void report(error, { kind: 'render', ref: this.state.ref, ...(componentStack ? { componentStack } : {}) })
    this.setState({ componentStack })
  }

  override componentDidUpdate(prev: ScreenBoundaryProps) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState(clear)
  }

  reset = () => this.setState(clear)

  override render() {
    const { error, ref, stale, reloading, componentStack } = this.state
    if (!error) return this.props.children
    return (
      <ScreenErrorFallback
        error={error}
        reference={ref}
        componentStack={componentStack}
        stale={stale}
        reloading={reloading}
        dev={this.props.dev ?? import.meta.env.DEV}
        onRetry={this.reset}
        onBack={this.props.onBack}
        onReload={() => window.location.reload()}
      />
    )
  }
}

export function ScreenErrorFallback(props: {
  error: Error
  reference: string
  componentStack?: string
  stale: boolean
  reloading: boolean
  dev: boolean
  onRetry: () => void
  onBack: () => void
  onReload: () => void
}) {
  const reload = (
    <Button variant="primary" onClick={props.onReload}>
      Reload
    </Button>
  )
  if (props.reloading) {
    return <ErrorNote title="Loading the new version…" message="This app was just updated." />
  }
  if (props.stale) {
    return (
      <ErrorNote
        title="A new version is out"
        message="This app was updated since you opened it. Reload to get the new version."
        actions={reload}
      />
    )
  }
  return (
    <ErrorNote
      title="This screen hit an error"
      message="Something on this screen broke. The rest of the app still works."
      onRetry={props.onRetry}
      actions={
        <>
          <Button onClick={props.onBack}>Go back</Button>
          {reload}
        </>
      }
      {...(props.dev ? { detail: `${props.error.message}${props.componentStack ? `\n${props.componentStack.trim()}` : ''}` } : {})}
    >
      {!props.dev && (
        <Text tone="muted" as="span">
          If it keeps happening, give your agent this reference:{' '}
          <Text mono as="span">
            {props.reference}
          </Text>
        </Text>
      )}
    </ErrorNote>
  )
}

/** The boundary as the app mounts it: it clears when the path changes, and Go back goes back (or home). */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const onBack = useCallback(() => {
    // react-router keeps the position in its history entries; 0 means this is the first page of the tab.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) void navigate(-1)
    else void navigate('/', { replace: true })
  }, [navigate])
  return (
    <ScreenBoundary resetKey={pathname} onBack={onBack}>
      {children}
    </ScreenBoundary>
  )
}
